"use client";

/**
 * AdjustCommitments — modal that replaces the legacy `BalanceEditMode`
 * inline grid. Implements the "Adjust commitments" overlay from the design
 * handoff (see /tmp/tin_handoff/design_handoff_balance_review):
 *
 *  - Weekly budget bar (sleep 56h + work 40h + commit X + free Y) showing
 *    whether the user's draft commitments fit within available time.
 *  - Per-area slider, range 0–900 minutes, step 15. A small amber tick on
 *    the slider track marks the currently-scheduled minutes — read-only;
 *    it shows the user how much of the target is already on the calendar
 *    before they save.
 *  - Local draft state; edits do NOT mutate `areas` until Save.
 *  - Backdrop click + ✕ + Esc all close.
 *
 * Drag-to-reorder priority is intentionally NOT shipped here — the LifeArea
 * model has no `priority` column yet; that's a separate PR. The handoff's
 * "drag to reorder" affordance is replaced with a shorter sub-line.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AreaIcon,
  areaKeyToIcon,
  fmt,
  type BalanceListArea,
} from "./BalanceList";
import styles from "./balance-list.module.css";

const FIXED_SLEEP_MINUTES = 56 * 60; // 56h sleep / week (handoff constant)
const FIXED_WORK_MINUTES = 40 * 60; // 40h work / week (handoff constant)
const WEEK_MINUTES = 7 * 24 * 60; // 10080
/** The pool of minutes left after sleep + work — the upper bound for
 *  the SUM of all area commitments. 168h - 56h - 40h = 72h = 4320 min. */
const FREE_BUDGET_MINUTES =
  WEEK_MINUTES - FIXED_SLEEP_MINUTES - FIXED_WORK_MINUTES;
const SLIDER_STEP = 15;

export interface AdjustCommitmentsAreaPatch {
  id: string;
  target_minutes_per_week: number | null;
}

export interface AdjustCommitmentsProps {
  /** Initial areas; copied into local draft on open. */
  areas: BalanceListArea[];
  /**
   * Called with the diff (areas whose target changed) when user clicks
   * Save. Caller wires this to `patchLifeAreasBatch`. Modal does not close
   * until the promise settles; on rejection the modal stays open and shows
   * an error.
   */
  onSave: (changed: AdjustCommitmentsAreaPatch[]) => Promise<void>;
  onClose: () => void;
}

interface DraftRow extends BalanceListArea {
  /** When true, treat target_minutes_per_week as user-set (even if 0). */
  committed: boolean;
}

function toDraft(areas: BalanceListArea[]): DraftRow[] {
  return areas.map((a) => ({
    ...a,
    committed: a.target_minutes_per_week != null,
  }));
}

export default function AdjustCommitments({
  areas,
  onSave,
  onClose,
}: AdjustCommitmentsProps) {
  const [draft, setDraft] = useState<DraftRow[]>(() => toDraft(areas));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-seed local draft if the host swaps in a new `areas` array (rare —
  // typically only fires when modal re-opens after a successful save).
  useEffect(() => {
    setDraft(toDraft(areas));
  }, [areas]);

  // Esc + backdrop close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalCommit = useMemo(
    () =>
      draft.reduce(
        (sum, row) =>
          sum + (row.committed ? row.target_minutes_per_week ?? 0 : 0),
        0,
      ),
    [draft],
  );
  const freeMinutes = Math.max(0, FREE_BUDGET_MINUTES - totalCommit);
  const overBudget = totalCommit > FREE_BUDGET_MINUTES;

  /** Per-row slider max = whatever's left in the budget if we *exclude*
   *  this row's current commitment. Snapped down to a clean 15-min grid.
   *  When other areas already saturate the free budget, this row's slider
   *  effectively locks at its current value (or 0). */
  const maxForRow = (row: DraftRow): number => {
    const otherCommits = draft.reduce((sum, r) => {
      if (r.id === row.id) return sum;
      return sum + (r.committed ? r.target_minutes_per_week ?? 0 : 0);
    }, 0);
    const remaining = Math.max(0, FREE_BUDGET_MINUTES - otherCommits);
    // Round down to slider step to keep the thumb on a valid stop.
    return Math.floor(remaining / SLIDER_STEP) * SLIDER_STEP;
  };

  const handleSliderChange = useCallback((id: string, value: number) => {
    setDraft((prev) =>
      prev.map((row) =>
        row.id === id
          ? { ...row, target_minutes_per_week: value, committed: true }
          : row,
      ),
    );
  }, []);

  const handleCommit = useCallback((id: string) => {
    setDraft((prev) =>
      prev.map((row) =>
        row.id === id
          ? { ...row, target_minutes_per_week: 60, committed: true }
          : row,
      ),
    );
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    const original = new Map(
      areas.map((a) => [a.id, a.target_minutes_per_week]),
    );
    const changed: AdjustCommitmentsAreaPatch[] = [];
    for (const row of draft) {
      const before = original.get(row.id) ?? null;
      const after = row.committed ? row.target_minutes_per_week : null;
      if (before !== after) {
        changed.push({ id: row.id, target_minutes_per_week: after });
      }
    }

    if (changed.length === 0) {
      setSaving(false);
      onClose();
      return;
    }

    try {
      await onSave(changed);
      // Host is responsible for closing on success — but as a safety net:
      onClose();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Could not save commitments.",
      );
      setSaving(false);
    }
  }, [areas, draft, onClose, onSave]);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Sticky-percentage helpers for the budget bar.
  const sleepPct = (FIXED_SLEEP_MINUTES / WEEK_MINUTES) * 100;
  const workPct = (FIXED_WORK_MINUTES / WEEK_MINUTES) * 100;
  const commitPct = Math.max(0, (totalCommit / WEEK_MINUTES) * 100);
  const freePct = Math.max(0, 100 - sleepPct - workPct - commitPct);

  return (
    <div
      className={styles["bl-modal-backdrop"]}
      onClick={onBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="adjust-commitments-title"
    >
      <div className={styles["bl-modal"]}>
        <header className={styles["bl-modal-head"]}>
          <div className={styles["bl-modal-eyebrow"]}>Adjust commitments</div>
          <h3 id="adjust-commitments-title" className={styles["bl-modal-title"]}>
            Edit your weekly balance
          </h3>
          <p className={styles["bl-modal-sub"]}>
            Set how many minutes per week each area should get. Saving updates
            next week&rsquo;s plan.
          </p>
          <button
            type="button"
            className={styles["bl-modal-close"]}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className={styles["bl-budget"]}>
          <div className={styles["bl-budget-label"]}>
            <span>Weekly budget</span>
            <span>
              {fmt(totalCommit)} committed · {fmt(freeMinutes)} free
            </span>
          </div>
          <div className={styles["bl-budget-bar"]}>
            <div
              className={`${styles["bl-budget-seg"]} ${styles["bl-budget-sleep"]}`}
              style={{ width: `${sleepPct}%` }}
              title={`Sleep ${fmt(FIXED_SLEEP_MINUTES)}`}
            />
            <div
              className={`${styles["bl-budget-seg"]} ${styles["bl-budget-work"]}`}
              style={{ width: `${workPct}%` }}
              title={`Work ${fmt(FIXED_WORK_MINUTES)}`}
            />
            <div
              className={`${styles["bl-budget-seg"]} ${styles["bl-budget-commit"]}`}
              style={{ width: `${commitPct}%` }}
              title={`Commit ${fmt(totalCommit)}`}
            />
            <div
              className={`${styles["bl-budget-seg"]} ${styles["bl-budget-free"]}`}
              style={{ width: `${freePct}%` }}
              title={`Free ${fmt(freeMinutes)}`}
            />
          </div>
          <div className={styles["bl-budget-legend"]}>
            <span className={styles["bl-budget-leg"]}>
              <i style={{ background: "#1f3329" }} />
              Sleep 56h
            </span>
            <span className={styles["bl-budget-leg"]}>
              <i style={{ background: "#5d7a6a" }} />
              Work 40h
            </span>
            <span className={styles["bl-budget-leg"]}>
              <i style={{ background: "#2d4a3e" }} />
              Commit {fmt(totalCommit)}
            </span>
            <span className={styles["bl-budget-leg"]}>
              <i
                style={{
                  background: "#ede5d4",
                  border: "1px solid var(--bl-rule)",
                }}
              />
              Free {fmt(freeMinutes)}
            </span>
          </div>
          {overBudget && (
            <p
              style={{
                fontSize: 11,
                color: "var(--bl-amber)",
                marginTop: 8,
                fontWeight: 500,
              }}
            >
              Heads up: total commitments now exceed the free hours in your
              week. Consider trimming a target.
            </p>
          )}
        </div>

        <ol className={styles["bl-adj-list"]}>
          {draft.map((row) => {
            const target = row.committed ? row.target_minutes_per_week ?? 0 : 0;
            const sched = row.scheduled_minutes_this_week ?? 0;
            const sliderMax = Math.max(target, maxForRow(row));
            const sliderRange = Math.max(SLIDER_STEP, sliderMax);
            // Tick position on a per-row variable scale (slider max
            // changes as other rows commit). Hidden when scheduled=0.
            const tickPct = Math.min(100, (sched / sliderRange) * 100);
            const atCap = row.committed && target >= sliderMax && sliderMax > 0;
            return (
              <li
                key={row.id}
                className={styles["bl-adj-row"]}
                data-testid={`adjust-row-${row.id}`}
              >
                <div className={styles["bl-adj-id"]}>
                  <span className={styles["bl-icon"]} aria-hidden>
                    <AreaIcon kind={row.icon} />
                  </span>
                  <span className={styles["bl-adj-name"]}>{row.name}</span>
                </div>

                {row.committed ? (
                  <div className={styles["bl-slider-wrap"]}>
                    <input
                      type="range"
                      min={0}
                      max={sliderRange}
                      step={SLIDER_STEP}
                      value={target}
                      onChange={(e) =>
                        handleSliderChange(row.id, Number(e.target.value))
                      }
                      className={styles["bl-slider"]}
                      aria-label={`${row.name} weekly target minutes (max ${fmt(sliderMax)})`}
                      data-testid={`adjust-slider-${row.id}`}
                    />
                    {sched > 0 && (
                      <span
                        className={styles["bl-slider-tick"]}
                        style={{ left: `${tickPct}%` }}
                        aria-hidden
                      />
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles["bl-adj-commit-btn"]}
                    onClick={() => handleCommit(row.id)}
                    disabled={maxForRow(row) === 0}
                    data-testid={`adjust-commit-${row.id}`}
                  >
                    {maxForRow(row) === 0 ? "Budget full" : "+ commit time"}
                  </button>
                )}

                <div className={styles["bl-adj-num"]}>
                  <div className={styles["bl-adj-target"]}>
                    {row.committed ? fmt(target) : "—"}
                  </div>
                  <div className={styles["bl-adj-sched"]}>
                    {atCap ? `cap ${fmt(sliderMax)}` : `sched ${fmt(sched)}`}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        <footer className={styles["bl-modal-foot"]}>
          <span className={styles["bl-modal-causality"]}>
            Priority → list order → next week&rsquo;s schedule
          </span>
          <div className={styles["bl-modal-actions"]}>
            {saveError && (
              <span style={{ color: "var(--bl-amber)", fontSize: 12 }}>
                {saveError}
              </span>
            )}
            <button
              type="button"
              className={styles["bl-btn-ghost"]}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles["bl-btn-primary"]}
              onClick={() => void handleSave()}
              disabled={saving}
              data-testid="adjust-save-button"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export { areaKeyToIcon };
